import { useState } from "react"
import { useRouter } from "next/navigation"
import { toast } from "@/components/ui/use-toast"
import { courseService } from "@/lib/services/course"
import { useBearStore } from "@/lib/stores/user"
import { useCart } from "@/lib/services/api"
import { axiosInstance } from "@/lib/axios"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { CourseDetail } from "@/lib/types/course/detail"

interface UseCourseActionsProps {
  course: CourseDetail
  initialIsWhitelisted?: boolean
}

export const useCourseActions = ({ course, initialIsWhitelisted = false }: UseCourseActionsProps) => {
  const router = useRouter()
  const { refetch } = useCart()
  const queryClient = useQueryClient()
  const { profileUser, setPendingCourse } = useBearStore()
  const [isLoading, setIsLoading] = useState(false)

  // Query for whitelist status
  const { data: isWhitelisted = initialIsWhitelisted } = useQuery({
    queryKey: ["whitelist", course.id],
    queryFn: async () => {
      if (!profileUser.name) return initialIsWhitelisted
      const res = await axiosInstance.get("/api/whitelist")
      return res.data.some((item: any) => item.item_id === course.id.toString())
    },
    enabled: !!profileUser.name && !initialIsWhitelisted,
  })

  // Mutation for toggling whitelist
  const whitelistMutation = useMutation({
    mutationFn: async () => {
      return courseService.toggleWhitelist(course.id.toString(), isWhitelisted, course.name)
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.setQueryData(["whitelist", course.id], response.data.isWhitelisted)
        toast({
          variant: isWhitelisted ? "warning" : "success",
          title: isWhitelisted ? "Removed" : "Success",
          description: response.message,
        })
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.message,
        })
        if (response.redirectUrl) {
          router.push(response.redirectUrl)
        }
      }
    },
  })

  const handleWhitelist = async () => {
    try {
      setIsLoading(true)
      await whitelistMutation.mutateAsync()
    } finally {
      setIsLoading(false)
    }
  }

  // Mutation for buying course
  const buyMutation = useMutation({
    mutationFn: async () => {
      return courseService.handleCourseAction(course, (courseId: string) => setPendingCourse(Number(courseId)))
    },
    onSuccess: (response) => {
      if (response.success) {
        if (response.message) {
          toast({
            title: "Success",
            description: response.message,
          })
        }
        if (response.redirectUrl) {
          router.push(response.redirectUrl)
        }
        refetch()
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.message,
        })
        if (response.redirectUrl) {
          router.push(response.redirectUrl)
        }
      }
    },
  })

  const handleBuy = async () => {
    try {
      setIsLoading(true)
      await buyMutation.mutateAsync()
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isWhitelisted,
    isLoading: isLoading || whitelistMutation.isPending || buyMutation.isPending,
    handleWhitelist,
    handleBuy
  }
} 